export type ConsentValue = "accepted" | "rejected";

type ConsentModeValue = "denied" | "granted";

type ConsentParams = {
  ad_storage: ConsentModeValue;
  ad_user_data: ConsentModeValue;
  ad_personalization: ConsentModeValue;
  analytics_storage: ConsentModeValue;
  wait_for_update?: number;
};

export const CONSENT_COOKIE = "resumotion_gdpr";

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;
const RESUMOTION_DOMAIN = ".resumotion.com";

export const DEFAULT_CONSENT: ConsentParams = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
  wait_for_update: 500,
};

export const GRANTED_CONSENT: ConsentParams = {
  ad_storage: "granted",
  ad_user_data: "granted",
  ad_personalization: "granted",
  analytics_storage: "granted",
};

export const DENIED_CONSENT: ConsentParams = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
};

const isBrowser = typeof document !== "undefined";

const isConsentValue = (value: string): value is ConsentValue =>
  value === "accepted" || value === "rejected";

export const getStoredConsent = (): ConsentValue | null => {
  if (!isBrowser) return null;

  const cookie = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${CONSENT_COOKIE}=`));

  if (!cookie) return null;

  const value = decodeURIComponent(cookie.split("=").slice(1).join("="));
  return isConsentValue(value) ? value : null;
};

export const setStoredConsent = (value: ConsentValue) => {
  if (!isBrowser) return;

  const cookieParts = [
    `${CONSENT_COOKIE}=${encodeURIComponent(value)}`,
    "path=/",
    `max-age=${COOKIE_MAX_AGE_SECONDS}`,
    "SameSite=Lax",
  ];

  if (window.location.hostname.endsWith("resumotion.com")) {
    cookieParts.push(`domain=${RESUMOTION_DOMAIN}`);
  }

  if (window.location.protocol === "https:") {
    cookieParts.push("Secure");
  }

  document.cookie = cookieParts.join("; ");
};

export const applyConsent = (value: ConsentValue) => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;

  window.gtag("consent", "update", value === "accepted" ? GRANTED_CONSENT : DENIED_CONSENT);
};
